import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { t } from '../../translations';
import { AuroraCachedApi } from '../../services/aurora-cache';
import { SubscriptionService } from '../../services/subscription-service';
import { LocationService, type StationSelection } from '../../services/location-service';
import type { Station } from '../../services/aurora-api';
import type { LocationUpdateEvent } from '../../app-index';
import '../../components/bottom-tabs';
import { LocalStorageService } from '../../services/local-storage';

@customElement('app-settings')
export class AppSettings extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        position: relative;
        background: #0D1726;
      }

      main {
        min-height: 100vh;
        padding: 1rem;
        padding-bottom: 80px; /* Fixed padding: 60px tabs + 20px extra space */
      }

      @supports (padding-bottom: env(safe-area-inset-bottom)) {
        main {
          padding-bottom: calc(80px + env(safe-area-inset-bottom));
        }
      }

      .settings-card {
        background: #142138;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .settings-card h2 {
        margin: 0 0 1rem 0;
        color: #ffffff;
      }

      .settings-group {
        margin-bottom: 1.5rem;
      }

      .settings-group h3 {
        margin: 0 0 0.5rem 0;
        color: #ffffff;
        opacity: 0.8;
        font-size: 1rem;
      }

      .settings-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }

      .settings-item:last-child {
        border-bottom: none;
      }

      .settings-item label {
        color: #ffffff;
        opacity: 0.8;
      }

      .settings-item select {
        padding: 0.25rem;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 4px;
        background: #0D1726;
        color: #ffffff;
      }

      .cache-button {
        background: #1e40af;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
      }

      .cache-button:hover {
        background: #1d4ed8;
      }

      .cache-info {
        font-size: 0.8rem;
        opacity: 0.7;
        margin-top: 0.25rem;
      }

      .location-button {
        background: #059669;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
      }

      .location-button:hover {
        background: #047857;
      }

      .location-button.revoke {
        background: #dc2626;
      }

      .location-button.revoke:hover {
        background: #b91c1c;
      }

      .location-info {
        font-size: 0.8rem;
        opacity: 0.7;
        margin-top: 0.25rem;
      }

      .notification-button {
        background: #2563eb;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
      }

      .notification-button:hover {
        background: #1d4ed8;
      }

      .notification-button.disable {
        background: #dc2626;
      }

      .notification-button.disable:hover {
        background: #b91c1c;
      }

      .notification-info {
        font-size: 0.8rem;
        opacity: 0.7;
        margin-top: 0.25rem;
      }

      .version-info {
        font-size: 0.8rem;
        opacity: 0.6;
        color: #ffffff;
        text-align: center;
        margin-top: 0.5rem;
      }

      .error-message {
        font-size: 0.8rem;
        color: #ef4444;
        margin-top: 0.25rem;
        opacity: 0.9;
      }

      .warning-message {
        font-size: 0.8rem;
        color: #ffa000;
        margin-top: 0.25rem;
        opacity: 0.9;
      }

      .station-selection {
        margin-top: 1rem;
      }

      .station-mode-selector {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;
      }

      .mode-option {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        transition: background-color 0.2s;
      }

      .mode-option:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      .mode-option.selected {
        background-color: rgba(34, 197, 94, 0.2);
        border-color: #22c55e;
      }

      .mode-option input[type="radio"] {
        margin: 0;
      }

      .stations-list {
        margin-top: 1rem;
      }

      .station-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .station-item:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }

      .station-item input[type="checkbox"] {
        margin: 0;
      }

      .station-info {
        flex: 1;
      }

      .station-name {
        font-weight: 500;
        margin-bottom: 0.25rem;
      }

      .station-distance {
        font-size: 0.9rem;
        opacity: 0.7;
        color: #40f9d1;
        font-weight: normal;
      }

      .stations-loading {
        text-align: center;
        padding: 1rem;
        opacity: 0.7;
      }

      .stations-empty {
        text-align: center;
        padding: 1rem;
        opacity: 0.7;
        color: #ef4444;
      }

      .about-content {
        font-size: 0.85rem;
        line-height: 1.5;
        color: #ffffff;
        opacity: 0.8;
      }

      .about-content p {
        margin: 0.5rem 0 0.25rem 0;
      }

      .about-content ul {
        margin: 0 0 0.75rem 0;
        padding-left: 1.5rem;
      }

      .about-content li {
        margin-bottom: 0.25rem;
      }

      .about-content dl {
        margin: 0 0 0.75rem 0;
      }

      .about-content dt {
        font-weight: bold;
        margin-top: 0.5rem;
      }

      .about-content dt:first-child {
        margin-top: 0;
      }

      .about-content dd {
        margin-left: 1.5rem;
        margin-bottom: 0.5rem;
      }
    `
  ];

  private language = localStorage.getItem('language') || 'en';

  @state() private cacheStatus: any = null;
  @state() private locationPermissionStatus: 'granted' | 'denied' | 'prompt' = 'prompt';
  @state() private location: { latitude: number; longitude: number } | null = null;
  @state() private notificationPermission: NotificationPermission = 'default';
  @state() private notificationSubscribed: boolean = false;
  @state() private selectedAuroraLevel: string = 'some';
  @state() private auroraLevelError: string = '';
  @state() private stations: Station[] = [];
  @state() private stationSelection: StationSelection = { mode: 'automatic', stationCodes: [] };
  @state() private stationSelectionError: string = '';
  @state() private stationsWithDistance: Array<Station & { distance?: number }> = [];
  @state() private notificationError: string = '';

  async connectedCallback() {
    super.connectedCallback();
    // Load cache status when component loads
    await this.loadCacheStatus();

    // Check notification permission status
    await this.checkNotificationPermission();

    // Load aurora level preference
    await this.loadAuroraLevel();

    // Load stations and station selection
    await this.loadStations();
    this.loadStationSelection();

    // Add event listeners to refresh status when page becomes visible
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);

    // Add event listener for location updates on window
    window.addEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
    window.addEventListener('location-permission', this.handleLocationPermissionUpdate.bind(this) as EventListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    window.removeEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
    window.removeEventListener('location-permission', this.handleLocationPermissionUpdate.bind(this) as EventListener);
  }

  private handleLocationUpdate(event: LocationUpdateEvent) {
    this.location = {
      latitude: event.detail.latitude,
      longitude: event.detail.longitude
    };
    this.calculateStationDistances();
  }

  private handleLocationPermissionUpdate(event: CustomEvent) {
    const granted = event.detail.granted;
    this.locationPermissionStatus = granted ? 'granted' : 'denied';

    if (!granted) {
      this.location = null;
      this.calculateStationDistances();
      // TODO if automatic mode is selected, unsubscribe from stations and switch to manual
    }
  }

  private async loadCacheStatus() {
    try {
      this.cacheStatus = await AuroraCachedApi.getCacheStatus();
    } catch (error) {
      console.error('Failed to load cache status:', error);
    }
  }

  private loadAuroraLevel() {
    this.selectedAuroraLevel = localStorage.getItem('aurora-level-preference')?.toLowerCase() || 'some';
  }

  private async loadStations() {
    try {
      this.stations = await AuroraCachedApi.getStations();
      console.log(`Loaded ${this.stations.length} stations for selection`);
      this.calculateStationDistances();
    } catch (error) {
      console.error('Failed to load stations:', error);
      this.stations = [];
      this.stationsWithDistance = [];
    }
  }

  private calculateStationDistances() {
    if (!this.location || this.stations.length === 0) {
      this.stationsWithDistance = [...this.stations];
      return;
    }

    // Calculate distances and sort by distance
    this.stationsWithDistance = this.stations.map(station => {
      const distance = LocationService.calculateDistance(
        this.location!.latitude,
        this.location!.longitude,
        station.coordinates.lat,
        station.coordinates.long
      );
      return { ...station, distance: Math.round(distance * 10) / 10 };
    }).sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }

  private loadStationSelection() {
    this.stationSelection = LocalStorageService.getStationSelection();
  }

  private handleLanguageChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.language = value;
    localStorage.setItem('language', value);
    window.dispatchEvent(new CustomEvent('language-changed', { detail: { language: value } }));
    this.requestUpdate(); // Refresh the component with new translations
  }

  private async handleAuroraLevelChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    const previousValue = this.selectedAuroraLevel;

    // Clear any previous error
    this.auroraLevelError = '';

    // Update UI immediately for responsiveness
    this.selectedAuroraLevel = value;

    // Update on server if subscribed
    if (this.notificationSubscribed) {
      try {
        const subscriptionService = SubscriptionService.getInstance();
        await subscriptionService.updateAuroraLevel(value);

        // Only save to local storage after successful server update
        localStorage.setItem('aurora-level-preference', value);
        console.log('Aurora level updated successfully');
      } catch (error) {
        // Revert UI change and show error if server update failed
        this.selectedAuroraLevel = previousValue;
        this.auroraLevelError = t('levelUpdateError');
        console.error('Error updating aurora level:', error);
      }
    } else {
      // If not subscribed, save locally immediately since no server sync needed
      localStorage.setItem('aurora-level-preference', value);
    }
  }

  private async handleStationModeChange(event: Event) {
    const value = (event.target as HTMLInputElement).value as 'automatic' | 'manual';
    const previousSelection = { ...this.stationSelection };

    // Clear any previous error
    this.stationSelectionError = '';

    // Update UI immediately
    this.stationSelection = { ...this.stationSelection, mode: value };

    if (value === 'automatic') {
      // Clear manual selections when switching to automatic
      this.stationSelection.stationCodes = [];
    }

    await this.updateStationSelection(previousSelection);
  }

  private async handleStationCheckboxChange(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const stationCode = checkbox.value;
    const previousSelection = { ...this.stationSelection };

    // Clear any previous error
    this.stationSelectionError = '';

    // Update station codes based on checkbox state
    if (checkbox.checked) {
      if (!this.stationSelection.stationCodes.includes(stationCode)) {
        this.stationSelection.stationCodes = [...this.stationSelection.stationCodes, stationCode];
      }
    } else {
      this.stationSelection.stationCodes = this.stationSelection.stationCodes.filter(code => code !== stationCode);
    }

    // Trigger re-render
    this.requestUpdate();

    await this.updateStationSelection(previousSelection);
  }

  private async updateStationSelection(previousSelection: StationSelection) {
    try {
      // Save selection locally
      LocalStorageService.saveStationSelection(this.stationSelection);

      // Update subscription if user is subscribed
      if (this.notificationSubscribed) {
        if (this.stationSelection.mode === 'automatic' && !this.location) {
          console.warn('🎯 Automatic mode selected but no location available - cannot update subscription');
          return;
        }

        let stationCodes = this.stationSelection.stationCodes;
        if (this.stationSelection.mode === 'automatic') {
          const closestStation = LocalStorageService.getClosestStationData();
          if (closestStation) {
            stationCodes = [closestStation.station.code];
          }
        }

        await SubscriptionService.getInstance().updateStationList(stationCodes);
      }
    } catch (error) {
      // Revert changes and show error
      this.stationSelection = previousSelection;
      this.stationSelectionError = t('stationUpdateError');
      console.error('Error updating station selection:', error);
    }
  }

  private async handleClearCache() {
    try {
      await AuroraCachedApi.clearAllCache();
      await this.loadCacheStatus();
      // Could add a toast notification here
      console.log('Cache cleared successfully');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  private async checkNotificationPermission() {
    // Check browser notification permission
    this.notificationPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';

    // Check if we have an active push subscription
    try {
      const subscriptionService = SubscriptionService.getInstance();
      const registration = await subscriptionService.registerServiceWorker();

      if (registration.pushManager) {
        const subscription = await registration.pushManager.getSubscription();
        this.notificationSubscribed = !!subscription;
      }
    } catch (error) {
      console.error('Error checking notification subscription:', error);
      this.notificationSubscribed = false;
    }
  }

  private handleVisibilityChange = () => {
    if (!document.hidden) {
      // Page became visible, refresh notification status
      this.checkNotificationPermission();
      // Clear aurora level error when page becomes visible
      this.auroraLevelError = '';
    }
  }

  private handleWindowFocus = () => {
    // Window got focus, refresh notification status
    this.checkNotificationPermission();
  }

  private async handleEnableNotifications() {
    try {
      // Clear any previous error
      this.notificationError = '';

      const subscriptionService = SubscriptionService.getInstance();

      // Request permission first
      const permission = await subscriptionService.requestPermission();
      this.notificationPermission = permission;

      if (permission === 'granted') {
        // Get VAPID public key from environment
        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        if (!vapidKey) {
          console.error('VAPID public key not found in environment variables');
          return;
        }

        // Subscribe to push notifications
        const subscription = await subscriptionService.subscribeToPushNotifications(vapidKey);
        this.notificationSubscribed = !!subscription;

        if (subscription) {
          console.log('Successfully subscribed to notifications');

          // Clear any previous aurora level error
          this.auroraLevelError = '';

          // Update aurora level on server after successful subscription
          await subscriptionService.updateAuroraLevel(this.selectedAuroraLevel);
          console.log('Aurora level set on server after subscription');

          // Update stations on server after successful subscription
          if (this.stationSelection.mode === 'automatic' && this.location) {
            const closestStation = LocalStorageService.getClosestStationData();
            if (closestStation) {
              await subscriptionService.updateStationList([closestStation.station.code]);
            }
          } else if (this.stationSelection.mode === 'manual' && this.stationSelection.stationCodes.length > 0) {
            await subscriptionService.updateStationList(this.stationSelection.stationCodes);
          }
        }
      }

      // Refresh status after enabling
      await this.checkNotificationPermission();
    } catch (error) {
      this.notificationError = t('notificationEnableError');
      console.error('Error enabling notifications:', error);
    }
  }

  private async handleDisableNotifications() {
    try {
      const subscriptionService = SubscriptionService.getInstance();
      await subscriptionService.unsubscribe();
      this.notificationSubscribed = false;
      console.log('Successfully unsubscribed from notifications');

      // Refresh status after disabling
      await this.checkNotificationPermission();
    } catch (error) {
      console.error('Error disabling notifications:', error);
    }
  }

  render() {
    return html`
      <main>
        <div class="settings-card">
          <h2>${t('settingsTitle')}</h2>

          <div class="settings-group">
            <h3>${t('language')}</h3>
            <div class="settings-item">
              <label for="language-select">${t('selectLanguage')}</label>
              <select id="language-select" @change=${this.handleLanguageChange} .value=${this.language}>
                <option value="en">English</option>
                <option value="no">Norsk</option>
                <option value="da">Dansk</option>
                <option value="sv">Svenska</option>
                <option value="kl">Kalaallisut</option>
                <option value="fo">Føroyskt</option>
              </select>
            </div>
          </div>

          <div class="settings-group">
            <h3>${t('location')}</h3>
            <div class="settings-item">
              <div class="location-info">
              ${this.locationPermissionStatus === 'denied' ? html`
                  ❌ ${t('accessDenied')}. ${t('changeBrowserSettings')}
                ` : html`
              ${this.location ? html`
                Lat: ${this.location.latitude.toFixed(4)}, Lon: ${this.location.longitude.toFixed(4)}
              ` : html`${t('locationNotAvailable')}`}
              `}
              </div>
            </div>
          </div>

          <div class="settings-group">
            <h3>${t('notifications')}</h3>
            <div class="settings-item">
              <div>
                <label>${t('pushNotifications')}</label>
                <div class="notification-info">
                  ${this.notificationPermission === 'granted' && this.notificationSubscribed ? html`
                    ✅ ${t('enabledAndSubscribed')}
                  ` : this.notificationPermission === 'granted' && !this.notificationSubscribed ? html`
                    ⚠️ ${t('permissionGrantedNotSubscribed')}
                  ` : this.notificationPermission === 'denied' ? html`
                    ❌ ${t('permissionDenied')}. ${t('changeBrowserSettings')}
                  ` : html`
                    ⏳ ${t('notRequested')}
                  `}
                </div>
                ${this.notificationError ? html`
                  <div class="error-message">${this.notificationError}</div>
                ` : ''}
              </div>
              ${this.notificationPermission === 'granted' && this.notificationSubscribed ? html`
                <button class="notification-button disable" @click=${this.handleDisableNotifications}>
                  ${t('disableNotifications')}
                </button>
              ` : this.notificationPermission !== 'denied' ?  html`
                <button class="notification-button" @click=${this.handleEnableNotifications}>
                  ${t('enableNotifications')}
                </button>
              ` : ''}
            </div>
          </div>

          ${this.notificationPermission === 'granted' && this.notificationSubscribed ? html`
            <div class="settings-group">
              <h3>${t('notificationLevel')}</h3>
              <div class="settings-item">
                <div>
                  <label for="aurora-level-select">${t('selectNotificationLevel')}</label>
                  ${this.auroraLevelError ? html`
                    <div class="error-message">${this.auroraLevelError}</div>
                  ` : ''}
                </div>
                <select
                  id="aurora-level-select"
                  @change=${this.handleAuroraLevelChange}
                  .value=${this.selectedAuroraLevel}
                >
                  <option value="some">${t('levelSome')}</option>
                  <option value="high">${t('levelHigh')}</option>
                  <option value="very_high">${t('levelVeryHigh')}</option>
                  <option value="extreme">${t('levelExtreme')}</option>
                </select>
              </div>
            </div>
          ` : ''}

          ${this.notificationPermission === 'granted' && this.notificationSubscribed ? html`
            <div class="settings-group">
              <h3>${t('stationSelection')}</h3>
              <div class="settings-item">
                <div class="station-selection">
                  <div class="station-mode-selector">
                    <label class="mode-option ${this.stationSelection.mode === 'automatic' ? 'selected' : ''}">
                      <input
                        type="radio"
                        name="station-mode"
                        value="automatic"
                        ?disabled=${this.location ? false : true}
                        .checked=${this.stationSelection.mode === 'automatic' && this.location !== null}
                        @change=${this.handleStationModeChange}
                      />
                      <span>${t('automaticMode')}</span>
                    </label>
                    <label class="mode-option ${this.stationSelection.mode === 'manual' ? 'selected' : ''}">
                      <input
                        type="radio"
                        name="station-mode"
                        value="manual"
                        .checked=${this.stationSelection.mode === 'manual'}
                        @change=${this.handleStationModeChange}
                      />
                      <span>${t('manualMode')}</span>
                    </label>
                  </div>

                  ${this.location === null ? html`
                    <div>
                      <span class="warning-message">
                      ${t('automaticModeRequiresLocation')}
                      </span>
                    </div>
                  ` : ''}

                  ${this.stationSelection.mode === 'manual' ? html`
                    <div class="stations-list">
                      ${this.stationsWithDistance.length === 0 ? html`
                        <div class="stations-loading">${t('loadingStations')}</div>
                      ` : this.stationsWithDistance.map(station => html`
                        <label class="station-item">
                          <input
                            type="checkbox"
                            value="${station.code}"
                            .checked=${this.stationSelection.stationCodes.includes(station.code)}
                            @change=${this.handleStationCheckboxChange}
                          />
                          <div class="station-info">
                            <div class="station-name">
                              ${station.name}${station.distance !== undefined ? html` <span class="station-distance">(${LocationService.formatDistance(station.distance)})</span>` : ''}
                            </div>
                          </div>
                        </label>
                      `)}
                    </div>
                  ` : html`
                  ${this.location !== null ? html`
                    <div class="mode-description">
                      ${t('automaticModeDescription')}
                    </div>
                  ` : ''}
                  `}

                  ${this.stationSelectionError ? html`
                    <div class="error-message">${this.stationSelectionError}</div>
                  ` : ''}
                </div>
              </div>
            </div>
          ` : ''}

          <div class="settings-group">
            <h3>${t('cache')}</h3>
            <div class="settings-item">
              <div>
                <label>${t('auroraDataCache')}</label>
                <div class="cache-info">
                  ${this.cacheStatus ? html`
                    ${t('stations')}: ${this.cacheStatus.stations.cached ? '✅ ' + t('cached') : '❌ ' + t('noCache')}<br>
                    ${t('polarOval')}: ${this.cacheStatus.polarOval.cached ? '✅ ' + t('cached') : '❌ ' + t('noCache')}<br>
                    ${t('ovationAurora')}: ${this.cacheStatus.ovationAurora.cached ? '✅ ' + t('cached') : '❌ ' + t('noCache')}<br>
                    ${t('polarElectroJet')}: ${this.cacheStatus.polarElectroJet.cached ? '✅ ' + t('cached') : '❌ ' + t('noCache')}<br>
                    Storage: ${Math.round(this.cacheStatus.cacheStats.estimatedSize / 1024)}KB ${t('storageUsed')}
                  ` : t('loading')}
                </div>
              </div>
              <button class="cache-button" @click=${this.handleClearCache}>
                ${t('clearCache')}
              </button>
            </div>
          </div>

          <div class="settings-group">
            <h3>About (only in english)</h3>
            <div class="about-content">
              <p>
                This app is developed by <strong>Narwhal ApS</strong> with contribution from the <strong>Division of Geomagnetism &amp; Geospace at DTU Space</strong>.
              </p>

              <p>
                <strong>Narwhal ApS</strong><br>
                Software Company in Copenhagen, Denmark<br>
                Contact: narwhal.tech@proton.me
              </p>

              <dl>
                <dt>Station activity level</dt> 
                <dd>Notifications are determined by magnetic field variations
                recorded in the nearest magnetometer station (marked with dots on the map). The
                magnetometer stations are monitored by: DTU Space (all stations in Greenland, The
                Faroe Islands and Denmark), Iceland University (the station in Iceland), Tromsø
                Geophysical Observatory at UiT – the Arctic University of Norway (all stations in
                Norway), Earth Sciences New Zealand - GNS Science (Scott Base and Eyrewell<sup>1</sup>), 
                The Geological Survey of Sweden - SGU (all stations in Sweden),
                and Finnish Meteorological Institute - FMI (all stations in Finland and the Tartu station). 
                The stations in Ittoqqortoormiit, Kulusuk, Sindal, Bornholm, Åland, Gotland, Hassela, Nora, Falkoping and Vaxsjo, are part of the MAG-SWE-DAN project, ESA Contract No. 4000128139/19/D/Cf.</dd>

                <dt>Feldstein-Starkov model</dt>
                <dd>Based on a model by Starkov et al. 1994<sup>2</sup>, described and tested by Sigernes et al. 2011<sup>3</sup>. Here, Hp30 index is used instead of Kp index.</dd>

                <dt>NOAA OVATION 30min forecast model</dt>
                <dd>Verision: OVATION Prime-2013. Based on a model by Newell et al, 2014<sup>4</sup> generated and distributed by National Oceanic and Atmospheric Administration (NOAA) National Weather Service, Space Weather Prediction Center (SWPC).</dd>

                <dt>Local polar electrojet over Greenland</dt>
                <dd>Based on a model by Olsen, 1996<sup>5</sup> and inspired by Gullikstad Johnsen, 2013<sup>6</sup>. Tested on Greenland ground magnetometer data by Steffensen, 2019<sup>7</sup>. The model is only visible during higher activity.</dd>

                <dt>Text</dt>
                <dd>By DTU Space, Hanne Thomasen and Nynne Lauritsen</dd>
              </dl>

              <p><strong>A special thanks to:</strong></p>
              <ul>
                <li>Dr. Karl M. Laundal (DTU) for scientific discussions and for sharing a script to find the subsolar point.</li>
                <li>Dr. Magnar Gullikstad Johnsen (Tromsø Geophysical Observatory, Faculty of Science and Technology UiT, The Arctic University of Norway) for scientific discussions and for the inspiration from the Near Real-time Auroral Electrojet Tracker: <a href="http://fox.phys.uit.no/AFFECTS/" target="_blank" rel="noopener">http://fox.phys.uit.no/AFFECTS/</a></li>
                <li>Professor Fred Sigernes for the inspiration based on his work on the northern lights and his aurora nowcast and forecast smartphone app: AuroraForecast3D that we highly recommend.</li>
                <li>GFZ Helmholtz Centre for Geosciences (GFZ), for derivation and distribution of the Hp30 index.</li>
                <li>Everyone who contributed to the translation of the app:
                  <ul>
                    <li>Greenlandic: Visit Greenland</li>
                    <li>Faroes: Poul J. Clementsen, Marni Mortensen, Martin V. Heinesen and Óluva Eidesgaard</li>
                    <li>English: Thom R. Edwards</li>
                    <li>Swedish: Anita &amp; Moa Nilsson</li>
                  </ul>
                </li>
              </ul>

              <p><strong>References:</strong></p>
              <ol>
                <li>GNS Science. (2024). Aotearoa New Zealand Continuous Raw, Basic and Derived Geomagnetic Data - GeoMag [Data set]. GNS Science. DOI: https://doi.org/10.21420/d1z9-k975?x=y</li>              
                <li>Starkov, G.V., (1994a). Mathematical model of the auroral boundaries, Geomag. Aeron., 34 (3), 331–336.</li>
                <li>Sigernes et al., (2011). Two methods to forecast auroral displays, J. Space Weather Space Clim. 1 A03, DOI: 10.1051/swsc/2011003.</li>
                <li>Newell P.T., Liou K., Zhang Y., Sotirelis T., Paxton L. J., MitchellE. J. (2014) OVATION Prime-2013: Extension of auroral precipitation model to higher disturbance levels. https://doi.org/10.1002/2014SW001056</li>
                <li>Olsen, N., (1996). A new tool for determining ionospheric currents from magnetic satellite data. In: Geophysical Research Letters 23.24, pages 3635-3638. </li>
                <li>Gullikstad Johnsen, M. (2013). Real-time determination and monitoring of the auroral electrojet boundaries. In: Journal of Space Weather and Space Climate 3, A28. doi: 10.1051/swsc/2013050.</li>
                <li>Steffensen, I., (2019). The dynamics of the polar ionospheric electrojet determined using ground magnetic data from Greenland, master thesis at DTU Space.</li>
              </ol>

              <p>
                <strong>Copyright:</strong> Narwhal ApS.
              </p>

              <p>
                <strong>Disclaimer:</strong> This app is provided by Narwhal ApS as is and with all faults. Narwhal ApS makes no representations or warranties of any kind concerning the safety, suitability, inaccuracies, typographical errors (etc.) of the app.
              </p>
            </div>
          </div>

          <div class="version-info">
            v${import.meta.env.VITE_APP_VERSION || '0.0.0'}
          </div>

        </div>
      </main>
      <bottom-tabs></bottom-tabs>
    `;
  }
}
